
export type DeviceStatus = 'registered' | 'in_process' | 'active';

export interface DeviceStatusInfo {
  status: DeviceStatus;
  label: string;
  description: string;
  color: string;
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline';
  hasProfile?: boolean;
}

export const getDeviceStatus = (serialNumber?: string, jssId?: string | number): DeviceStatus => {
  const hasSerial = serialNumber && serialNumber.trim() !== '';
  const hasJssId = jssId && String(jssId).trim() !== '';

  if (!hasSerial) {
    return 'registered';
  } else if (hasSerial && !hasJssId) {
    return 'in_process';
  } else {
    return 'active';
  }
};

export const getDeviceStatusInfo = (status: DeviceStatus, hasProfile?: boolean): DeviceStatusInfo => {
  switch (status) {
    case 'registered':
      return {
        status: 'registered',
        label: 'Registered',
        description: 'Device is registered but not yet configured',
        color: 'text-blue-600',
        badgeVariant: 'outline',
        hasProfile: undefined
      };
    case 'in_process':
      return {
        status: 'in_process',
        label: 'Prepared',
        description: 'Device configuration is prepared and ready',
        color: 'text-yellow-600',
        badgeVariant: 'secondary',
        hasProfile: undefined
      };
    case 'active':
      if (hasProfile === false) {
        return {
          status: 'active',
          label: 'Active (no profile)',
          description: 'Device is active but has no profile assigned',
          color: 'text-teal-600',
          badgeVariant: 'outline',
          hasProfile: false
        };
      }
      return {
        status: 'active',
        label: 'Active',
        description: 'Device is fully configured and active',
        color: 'text-green-600',
        badgeVariant: 'default',
        hasProfile: true
      };
  }
};

export const getDeviceStatusBadgeColor = (status: DeviceStatus, hasProfile?: boolean): string => {
  switch (status) {
    case 'registered':
      return 'bg-blue-100 text-blue-800';
    case 'in_process':
      return 'bg-yellow-100 text-yellow-800';
    case 'active':
      if (hasProfile === false) {
        return 'bg-teal-100 text-teal-800';
      }
      return 'bg-green-100 text-green-800';
  }
};

export const getRadixBadgeColor = (status: DeviceStatus, hasProfile?: boolean): "blue" | "amber" | "green" | "cyan" => {
  switch (status) {
    case 'registered':
      return 'blue';
    case 'in_process':
      return 'amber';
    case 'active':
      if (hasProfile === false) {
        return 'cyan';
      }
      return 'green';
  }
};
